import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Calendar, Mail, ArrowLeft, RotateCcw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { EASE } from "@/components/Reveal";
import { waLink, WHATSAPP_NUMBER } from "@/config";

const API = process.env.REACT_APP_BACKEND_URL;

const WELCOME_OLIVIA = `Hello! I'm Olivia from ANALYTROVA. 👋
Welcome to our website!
I'm your virtual AI assistant, and I'm here to help you find the right solutions for your business.
I'll ask you a few simple questions to understand your requirements. Please select one of the four options for each question, and I'll guide you through the next steps.
Let's get started!`;

const SERVICE_PATHS = {
    "Data Analytics & Dashboards": [
        "Data Cleaning & Organization",
        "Excel Reports & Dashboards",
        "Power BI Dashboards",
        "Sales & Profit Analysis",
    ],
    "Website Design & Development": [
        "Business Website",
        "Portfolio Website",
        "E-commerce Website",
        "Website Redesign",
    ],
    "Branding & Logo Design": [
        "New Business Logo",
        "Complete Brand Identity",
        "Social Media Creatives",
        "Business Branding Package",
    ],
    "Digital Marketing & AI Videos": [
        "AI Promotional Videos",
        "Social Media Content Creation",
        "Digital Marketing Creatives",
        "Social Media Marketing Support",
    ],
};

const QUESTIONS = [
    {
        key: "service",
        q: "Which service are you looking for?",
        options: Object.keys(SERVICE_PATHS),
    },
    {
        key: "business_type",
        q: "What type of business do you have?",
        options: ["Small Business or Local Shop", "Startup or New Business", "Established Company", "Individual or Personal Brand"],
    },
    {
        key: "specific_service",
        q: "What specific service do you need?",
        dynamic: (answers) => SERVICE_PATHS[answers.service] || [],
    },
    {
        key: "timeline",
        q: "When would you like to start?",
        options: ["Immediately", "Within One Week", "Within One Month", "Just Exploring"],
    },
    {
        key: "contact_method",
        q: "How would you prefer to contact us?",
        options: ["WhatsApp Message", "WhatsApp Call", "Phone Call", "Email"],
    },
];

const LETTERS = ["A", "B", "C", "D"];
const WELCOME_AI = "Hi! I'm Olivia, Analytrova's AI assistant. 👋 Ask me anything about our services, pricing or timelines — I can also help you book a free 30-minute consultation.";

// ---------- Free AI chat mode ----------
function AiChat({ messages, setMessages, typing, onSend }) {
    const [input, setInput] = useState("");
    const send = () => {
        const content = input.trim();
        if (!content || typing) return;
        setInput("");
        onSend(content);
    };
    return (
        <>
            <div className="grow overflow-y-auto px-4 py-4 space-y-3" data-testid="chatbot-messages">
                {messages.map((m, i) =>
                    m.content ? (
                        <div
                            key={i}
                            className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                                m.role === "user"
                                    ? "ml-auto bg-burgundy text-cream rounded-br-md"
                                    : "bg-white/80 border border-burgundy/10 text-charcoal rounded-bl-md"
                            }`}
                            data-testid={`chatbot-msg-${m.role}-${i}`}
                        >
                            {m.content}
                        </div>
                    ) : null
                )}
                {typing && (
                    <div className="inline-flex items-center gap-1.5 bg-white/80 border border-burgundy/10 rounded-2xl rounded-bl-md px-4 py-3" data-testid="chatbot-typing">
                        {[0, 1, 2].map((d) => (
                            <span key={d} className="w-1.5 h-1.5 rounded-full bg-burgundy/50 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                        ))}
                    </div>
                )}
            </div>
            <div className="shrink-0 border-t border-burgundy/10 bg-cream p-3 flex items-center gap-2">
                <input
                    className="field !py-2.5 flex-1"
                    placeholder="Type your message..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    data-testid="chatbot-input"
                />
                <button
                    onClick={send}
                    aria-label="Send message"
                    className="w-11 h-11 shrink-0 rounded-full bg-burgundy text-cream flex items-center justify-center hover:bg-burgundy-dark transition-colors disabled:opacity-50"
                    disabled={typing}
                    data-testid="chatbot-send"
                >
                    <Send className="w-4 h-4" />
                </button>
            </div>
        </>
    );
}

// ---------- Olivia guided flow ----------
function OliviaFlow({ onSwitchToAi }) {
    const [log, setLog] = useState([]);
    const [phase, setPhase] = useState("boot"); // boot | question | details | summary
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [transcript, setTranscript] = useState([]);
    const [showTyping, setShowTyping] = useState(true);
    const [details, setDetails] = useState({ name: "", business: "", phone: "", email: "", project_description: "" });
    const [errors, setErrors] = useState({});
    const [callbackDone, setCallbackDone] = useState(false);
    const [callbackDetails, setCallbackDetails] = useState({ name: "", business: "", phone: "" });
    const [callbackError, setCallbackError] = useState("");
    const [callbackBusy, setCallbackBusy] = useState(false);
    const timers = useRef([]);
    const leadId = useRef(null);
    const scrollRef = useRef(null);

    const clearTimers = () => {
        timers.current.forEach((t) => clearTimeout(t));
        timers.current = [];
    };
    useEffect(() => clearTimers, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [log, showTyping, phase]);

    const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

    const pushOlivia = (text, then) => {
        setShowTyping(true);
        later(() => {
            setShowTyping(false);
            setLog((prev) => [...prev, { type: "olivia", text }]);
            if (then) later(then, 350);
        }, 900);
    };

    // boot sequence
    useEffect(() => {
        later(() => {
            setLog([{ type: "olivia", text: WELCOME_OLIVIA }]);
            later(() => {
                setPhase("question");
                setStep(0);
                setLog((prev) => [...prev, { type: "olivia", text: QUESTIONS[0].q }]);
                setShowTyping(false);
            }, 1000);
        }, 500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveProgress = async (patch, create = false) => {
        try {
            if (create || !leadId.current) {
                const { data } = await axios.post(`${API}/api/chat-leads`, patch);
                leadId.current = data.id;
            } else {
                await axios.patch(`${API}/api/chat-leads/${leadId.current}`, patch);
            }
        } catch (e) {
            console.error(e);
            toast.error("We couldn't save your progress on our side — your answers are still safe here in the chat.");
        }
    };

    const answer = (label) => {
        if (phase !== "question" || showTyping) return;
        const q = QUESTIONS[step];
        const newAnswers = { ...answers, [q.key]: label };
        const newTranscript = [...transcript, { q: q.q, a: label }];
        setAnswers(newAnswers);
        setTranscript(newTranscript);
        setLog((prev) => [...prev, { type: "user", text: label }]);

        if (step === 0) {
            saveProgress({ service: label, transcript: newTranscript }, true);
        } else {
            saveProgress({ [q.key]: label, transcript: newTranscript });
        }

        if (step < QUESTIONS.length - 1) {
            setPhase("question");
            setShowTyping(true);
            later(() => {
                setShowTyping(false);
                setStep(step + 1);
                setLog((prev) => [...prev, { type: "olivia", text: QUESTIONS[step + 1].q }]);
            }, 950);
        } else {
            pushOlivia(
                "Perfect! 🌟 To connect you with the right person on our team, please share your contact details below. Your name, business name and phone number are required — email is optional. I'll only use them to respond to your enquiry.",
                () => setPhase("details")
            );
        }
    };

    const submitDetails = (e) => {
        e.preventDefault();
        const errs = {};
        if (!details.name.trim() || !details.business.trim() || !/^[+]?[0-9\s\-()]{8,16}$/.test(details.phone.trim())) {
            errs.form = "Please complete your name, business name and phone number before continuing.";
        }
        if (!errs.form && details.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email.trim())) {
            errs.email = "Please enter a valid email address.";
        }
        setErrors(errs);
        if (Object.keys(errs).length) return;

        saveProgress({
            ...details,
            completed: true,
            transcript: [...transcript],
        });
        setPhase("summary");
    };

    const goBack = () => {
        if (phase === "summary") {
            setPhase("details");
            return;
        }
        if (phase === "details") {
            setPhase("question");
            setStep(QUESTIONS.length - 1);
            setLog((prev) => [...prev, { type: "olivia", text: QUESTIONS[QUESTIONS.length - 1].q }]);
            return;
        }
        if (step === 0 || showTyping) return;
        const prevStep = step - 1;
        const q = QUESTIONS[step];
        setTranscript((prev) => prev.slice(0, -1));
        setAnswers((prev) => {
            const next = { ...prev };
            delete next[q.key];
            return next;
        });
        setLog((prev) => prev.slice(0, -2));
        setPhase("question");
        setShowTyping(true);
        later(() => {
            setShowTyping(false);
            setStep(prevStep);
            setLog((prev) => [...prev, { type: "olivia", text: QUESTIONS[prevStep].q }]);
        }, 800);
    };

    const restart = () => {
        clearTimers();
        leadId.current = null;
        setLog([]);
        setAnswers({});
        setTranscript([]);
        setDetails({ name: "", business: "", phone: "", email: "", project_description: "" });
        setErrors({});
        setCallbackDone(false);
        setCallbackDetails({ name: "", business: "", phone: "" });
        setCallbackError("");
        setStep(0);
        setPhase("boot");
        setShowTyping(true);
        later(() => {
            setShowTyping(false);
            setLog([{ type: "olivia", text: WELCOME_OLIVIA }]);
            later(() => {
                setPhase("question");
                setStep(0);
                setLog((prev) => [...prev, { type: "olivia", text: QUESTIONS[0].q }]);
            }, 1000);
        }, 400);
    };

    const currentQ = QUESTIONS[step];
    const options = currentQ && currentQ.dynamic ? currentQ.dynamic(answers) : currentQ?.options || [];

    const summaryText = [
        "Hello Analytrova! 👋 Here's my project summary:",
        answers.service && `Service: ${answers.service}`,
        answers.business_type && `Business Type: ${answers.business_type}`,
        answers.specific_service && `Required Service: ${answers.specific_service}`,
        answers.timeline && `Timeline: ${answers.timeline}`,
        answers.contact_method && `Preferred Contact Method: ${answers.contact_method}`,
        details.name && `Name: ${details.name}`,
        details.business && `Business: ${details.business}`,
        details.phone && `Phone: ${details.phone}`,
        details.email && `Email: ${details.email}`,
    ]
        .filter(Boolean)
        .join("\n");

    const submitCallback = async (e) => {
        e.preventDefault();
        if (!callbackDetails.name.trim() || !callbackDetails.business.trim() || !callbackDetails.phone.trim()) {
            setCallbackError("Please complete your name, business name and phone number before continuing.");
            return;
        }
        setCallbackError("");
        setCallbackBusy(true);
        try {
            const { data } = await axios.post(`${API}/api/callback`, {
                ...callbackDetails,
                lead_id: leadId.current || "",
            });
            await saveProgress({ callback_requested: true, contact_method: "Phone Call" });
            setCallbackDone(true);
            setPhase("summary");
            setLog((prev) => [
                ...prev,
                { type: "olivia", text: `Thank you, ${callbackDetails.name.split(" ")[0]}! Your callback request has been received — our team will call you back within 24 hours. 🌟` },
            ]);
            toast.success(data.email_sent === false
                ? "Callback saved — email notification unavailable right now, but our team will call you."
                : "Callback requested — we'll call you within 24 hours.");
        } catch (err) {
            console.error(err);
            toast.error("Couldn't submit the callback request — please try again or WhatsApp us.");
        } finally {
            setCallbackBusy(false);
        }
    };

    return (
        <div className="grow flex flex-col min-h-0">
            {/* progress + controls */}
            <div className="shrink-0 px-4 pt-3 pb-2 flex items-center gap-3 bg-cream/60 border-b border-burgundy/10">
                <button
                    onClick={goBack}
                    disabled={phase === "boot" || (phase === "question" && step === 0)}
                    className="text-[11px] font-medium text-burgundy inline-flex items-center gap-1 disabled:opacity-30"
                    data-testid="olivia-back"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <div className="grow">
                    <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-charcoal/50" data-testid="olivia-progress">
                        {phase === "question" ? `Step ${step + 1} of ${QUESTIONS.length}` : phase === "details" ? "Your Details" : phase === "callback" ? "Callback Request" : "Summary"}
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-burgundy/10 overflow-hidden">
                        <div
                            className="h-full bg-burgundy rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(((phase === "question" ? step : QUESTIONS.length) / QUESTIONS.length) * 100, 100)}%` }}
                        />
                    </div>
                </div>
                <button
                    onClick={restart}
                    className="text-[11px] font-medium text-charcoal/50 inline-flex items-center gap-1 hover:text-burgundy transition-colors"
                    data-testid="olivia-restart-top"
                >
                    <RotateCcw className="w-3.5 h-3.5" /> Restart
                </button>
            </div>

            {/* messages */}
            <div ref={scrollRef} className="grow overflow-y-auto px-4 py-4 space-y-3 min-h-0" data-testid="olivia-messages">
                {log.map((m, i) =>
                    m.type === "olivia" ? (
                        <div key={i} className="max-w-[88%] rounded-2xl rounded-bl-md bg-white/80 border border-burgundy/10 text-charcoal px-4 py-3 text-sm leading-relaxed whitespace-pre-line" data-testid={`olivia-msg-${i}`}>
                            {m.text}
                        </div>
                    ) : (
                        <div key={i} className="max-w-[85%] ml-auto rounded-2xl rounded-br-md bg-burgundy text-cream px-4 py-2.5 text-sm" data-testid={`olivia-user-${i}`}>
                            {m.text}
                        </div>
                    )
                )}

                {showTyping && (
                    <div className="inline-flex items-center gap-1.5 bg-white/80 border border-burgundy/10 rounded-2xl rounded-bl-md px-4 py-3" data-testid="olivia-typing">
                        {[0, 1, 2].map((d) => (
                            <span key={d} className="w-1.5 h-1.5 rounded-full bg-burgundy/50 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                        ))}
                    </div>
                )}

                {/* options */}
                {phase === "question" && !showTyping && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="grid grid-cols-1 gap-2 pt-1"
                    >
                        {options.map((opt, i) => (
                            <button
                                key={opt}
                                onClick={() => answer(opt)}
                                className="group flex items-center gap-3 text-left bg-white/80 border border-burgundy/15 rounded-xl px-4 py-3 text-sm text-charcoal hover:border-burgundy hover:bg-burgundy hover:text-cream transition-colors"
                                data-testid={`olivia-option-${step}-${i}`}
                            >
                                <span className="w-6 h-6 shrink-0 rounded-full border border-current/30 bg-olive/25 text-burgundy group-hover:bg-cream/20 group-hover:text-cream flex items-center justify-center text-[11px] font-semibold">
                                    {LETTERS[i]}
                                </span>
                                {opt}
                            </button>
                        ))}
                    </motion.div>
                )}

                {/* details form */}
                {phase === "details" && !showTyping && (
                    <motion.form
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        onSubmit={submitDetails}
                        className="bg-white/80 border border-burgundy/15 rounded-2xl p-4 space-y-3"
                        data-testid="olivia-details-form"
                    >
                        <div>
                            <label className="field-label" htmlFor="ol-name">Full Name *</label>
                            <input id="ol-name" className="field !py-2.5" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} placeholder="Your full name" data-testid="olivia-name" />
                            {errors.name && <p className="mt-1 text-xs text-burgundy">{errors.name}</p>}
                        </div>
                        <div>
                            <label className="field-label" htmlFor="ol-business">Business Name *</label>
                            <input id="ol-business" className="field !py-2.5" value={details.business} onChange={(e) => setDetails({ ...details, business: e.target.value })} placeholder="Your business name" data-testid="olivia-business" />
                        </div>
                        <div>
                            <label className="field-label" htmlFor="ol-phone">Phone Number *</label>
                            <input id="ol-phone" type="tel" className="field !py-2.5" value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} placeholder="+91 ..." data-testid="olivia-phone" />
                            {errors.phone && <p className="mt-1 text-xs text-burgundy">{errors.phone}</p>}
                        </div>
                        <div>
                            <label className="field-label" htmlFor="ol-email">Email Address (optional)</label>
                            <input id="ol-email" type="email" className="field !py-2.5" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} placeholder="you@example.com" data-testid="olivia-email" />
                            {errors.email && <p className="mt-1 text-xs text-burgundy">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="field-label" htmlFor="ol-project">Project Requirements (optional)</label>
                            <textarea id="ol-project" rows="2" className="field resize-none" value={details.project_description} onChange={(e) => setDetails({ ...details, project_description: e.target.value })} placeholder="Tell us a little about your project..." data-testid="olivia-project" />
                        </div>
                        {errors.form && (
                            <p className="text-xs font-medium text-burgundy bg-burgundy/8 border border-burgundy/20 rounded-lg px-3 py-2" data-testid="olivia-details-error">
                                {errors.form}
                            </p>
                        )}
                        <p className="text-[11px] text-charcoal/50 leading-relaxed">
                            We only use these details to respond to your enquiry and prepare your project summary — nothing else.
                        </p>
                        <button type="submit" className="btn btn-burgundy w-full justify-center !py-2.5" data-testid="olivia-submit-details">
                            Share Details
                        </button>
                    </motion.form>
                )}

                {/* summary */}
                {phase === "summary" && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="bg-white/80 border border-gold-deep/40 rounded-2xl p-4 text-sm"
                        data-testid="olivia-summary"
                    >
                        <p className="text-charcoal">Thank you for choosing ANALYTROVA! ✨</p>
                        <p className="mt-1 text-charcoal/70">Here is your project summary:</p>
                        <ul className="mt-3 space-y-1.5" data-testid="olivia-summary-list">
                            {[
                                ["Service", answers.service],
                                ["Business Type", answers.business_type],
                                ["Required Service", answers.specific_service],
                                ["Timeline", answers.timeline],
                                ["Preferred Contact Method", answers.contact_method],
                                ["Name", details.name],
                                ["Business", details.business],
                                ["Phone", details.phone],
                                details.email && ["Email", details.email],
                            ]
                                .filter(Boolean)
                                .map(([k, v]) => (
                                    <li key={k} className="flex gap-2">
                                        <span className="text-charcoal/55 shrink-0">{k}:</span>
                                        <span className="font-medium text-charcoal">{v}</span>
                                    </li>
                                ))}
                        </ul>
                        <p className="mt-3 text-charcoal/70">Thank you! Your project requirements are ready to share with our team.</p>

                        <div className="mt-4 space-y-2">
                            <a
                                href={waLink(summaryText)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => saveProgress({ whatsapp_clicked: true })}
                                className="btn btn-olive w-full justify-center !py-2.5 !text-xs"
                                data-testid="olivia-whatsapp"
                            >
                                CONTACT US ON WHATSAPP
                            </a>
                            <button
                                onClick={() => {
                                    setCallbackDetails({ name: details.name || "", business: details.business || "", phone: details.phone || "" });
                                    setPhase("callback");
                                }}
                                disabled={callbackDone}
                                className="btn btn-burgundy w-full justify-center !py-2.5 !text-xs disabled:opacity-60"
                                data-testid="olivia-callback"
                            >
                                {callbackDone ? "Callback Requested ✓" : "REQUEST A CALLBACK"}
                            </button>
                            <button
                                onClick={restart}
                                className="w-full text-center text-xs text-charcoal/55 underline underline-offset-2 hover:text-burgundy py-1 transition-colors"
                                data-testid="olivia-restart"
                            >
                                START AGAIN
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* callback form */}
                {phase === "callback" && !showTyping && (
                    <motion.form
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        onSubmit={submitCallback}
                        className="bg-white/80 border border-burgundy/15 rounded-2xl p-4 space-y-3"
                        data-testid="olivia-callback-form"
                    >
                        <p className="text-sm font-medium text-charcoal">Request a Callback</p>
                        <div>
                            <label className="field-label" htmlFor="cb-name">Full Name *</label>
                            <input id="cb-name" className="field !py-2.5" value={callbackDetails.name} onChange={(e) => setCallbackDetails({ ...callbackDetails, name: e.target.value })} placeholder="Your full name" data-testid="callback-name" />
                        </div>
                        <div>
                            <label className="field-label" htmlFor="cb-business">Business Name *</label>
                            <input id="cb-business" className="field !py-2.5" value={callbackDetails.business} onChange={(e) => setCallbackDetails({ ...callbackDetails, business: e.target.value })} placeholder="Your business name" data-testid="callback-business" />
                        </div>
                        <div>
                            <label className="field-label" htmlFor="cb-phone">Phone Number *</label>
                            <input id="cb-phone" type="tel" className="field !py-2.5" value={callbackDetails.phone} onChange={(e) => setCallbackDetails({ ...callbackDetails, phone: e.target.value })} placeholder="+91 ..." data-testid="callback-phone" />
                        </div>
                        {callbackError && <p className="text-xs font-medium text-burgundy" data-testid="callback-error">{callbackError}</p>}
                        <div className="flex gap-2">
                            <button type="submit" className="btn btn-burgundy grow justify-center !py-2.5" disabled={callbackBusy} data-testid="callback-submit">
                                {callbackBusy ? "Sending..." : "Request Callback"}
                            </button>
                            <button type="button" onClick={() => setPhase("summary")} className="btn btn-outline !py-2.5 !px-4" data-testid="callback-cancel">
                                Cancel
                            </button>
                        </div>
                    </motion.form>
                )}
            </div>

            {/* switcher */}
            <div className="shrink-0 border-t border-burgundy/10 bg-cream px-4 py-2 text-center">
                <button onClick={onSwitchToAi} className="text-[11px] text-charcoal/50 hover:text-burgundy underline underline-offset-2 transition-colors" data-testid="olivia-switch-ai">
                    Prefer to ask your own question? Chat freely with AI
                </button>
            </div>
        </div>
    );
}

// ---------- main ----------
export default function Chatbot() {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("flow"); // flow | ai
    const [aiMessages, setAiMessages] = useState([{ role: "assistant", content: WELCOME_AI }]);
    const [typing, setTyping] = useState(false);
    const scrollRef = useRef(null);
    const abortRef = useRef(null);

    useEffect(() => {
        const opener = () => setOpen(true);
        window.addEventListener("analytrova:open-chat", opener);
        return () => window.removeEventListener("analytrova:open-chat", opener);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [aiMessages, typing, open, mode]);

    const sendAi = async (content) => {
        if (!content.trim() || typing) return;
        const history = [...aiMessages, { role: "user", content }];
        setAiMessages([...history, { role: "assistant", content: "" }]);
        setTyping(true);
        const controller = new AbortController();
        abortRef.current = controller;
        let gotFirst = false;
        try {
            const res = await fetch(`${API}/api/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ messages: history.slice(-12) }),
                signal: controller.signal,
            });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop();
                for (const part of parts) {
                    const line = part.trim();
                    if (!line.startsWith("data:")) continue;
                    const payload = JSON.parse(line.slice(5).trim());
                    if (payload.delta) {
                        if (!gotFirst) {
                            gotFirst = true;
                            setTyping(false);
                        }
                        setAiMessages((prev) => {
                            const next = [...prev];
                            const last = next[next.length - 1];
                            next[next.length - 1] = { ...last, content: last.content + payload.delta };
                            return next;
                        });
                    }
                }
            }
        } catch (err) {
            if (err?.name !== "AbortError") {
                setAiMessages((prev) => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last && last.role === "assistant" && !last.content) {
                        next[next.length - 1] = {
                            ...last,
                            content:
                                "Sorry, something went wrong on my side. Please use the enquiry form or WhatsApp us at +91 81257 61486 — we respond within 24 hours.",
                        };
                    }
                    return next;
                });
            }
        } finally {
            setTyping(false);
        }
    };

    return (
        <>
            {/* Floating chatbot button */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className="group fixed bottom-5 right-5 z-[80]"
                    aria-label="Chat with Olivia, our AI assistant"
                    data-testid="chatbot-float"
                >
                    <span className="chat-pulse" aria-hidden="true" />
                    <span className="relative w-14 h-14 rounded-full bg-burgundy text-cream flex items-center justify-center shadow-[0_14px_34px_-10px_rgba(90,0,16,0.6)] transition-transform duration-300 group-hover:scale-110">
                        <Bot className="w-6 h-6" />
                        <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-olive border-2 border-cream" aria-hidden="true" />
                    </span>
                    <span className="pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-charcoal text-cream text-xs px-3 py-2 rounded-lg opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                        Chat with Olivia — AI assistant
                    </span>
                </button>
            )}

            {/* Chat window (stays mounted so progress is never lost) */}
            <div
                className={`fixed z-[85] bottom-4 right-4 sm:bottom-5 sm:right-5 w-[calc(100vw-2rem)] max-w-[400px] h-[min(620px,calc(100dvh-5rem))] bg-cream-soft border border-burgundy/15 rounded-2xl shadow-[0_30px_80px_-30px_rgba(90,0,16,0.5)] flex flex-col overflow-hidden transition-all duration-300 ${
                    open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
                }`}
                data-testid="chatbot-window"
            >
                {/* header */}
                <div className="bg-burgundy text-cream px-5 py-4 flex items-center gap-3 shrink-0">
                    <span className="w-9 h-9 rounded-full bg-cream/15 flex items-center justify-center">
                        <Bot className="w-5 h-5" />
                    </span>
                    <div className="grow">
                        <div className="font-medium leading-tight">Olivia</div>
                        <div className="text-[11px] text-cream/70 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-olive-soft inline-block" aria-hidden="true" />
                            Online — replies instantly
                        </div>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        aria-label="Close chat"
                        className="w-8 h-8 rounded-full hover:bg-cream/15 flex items-center justify-center transition-colors"
                        data-testid="chatbot-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* mode toggle */}
                <div className="shrink-0 bg-cream/70 border-b border-burgundy/10 px-4 py-2 flex gap-2">
                    {[
                        { id: "flow", label: "Guided Flow", testid: "chatbot-mode-flow" },
                        { id: "ai", label: "AI Chat", testid: "chatbot-mode-ai" },
                    ].map((m) => (
                        <button
                            key={m.id}
                            onClick={() => setMode(m.id)}
                            className={`text-[11px] font-semibold tracking-wide px-3 py-1.5 rounded-full transition-colors ${
                                mode === m.id ? "bg-burgundy text-cream" : "text-charcoal/60 hover:text-burgundy"
                            }`}
                            data-testid={m.testid}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>

                {/* body */}
                {mode === "flow" ? (
                    <OliviaFlow onSwitchToAi={() => setMode("ai")} />
                ) : (
                    <AiChat messages={aiMessages} setMessages={setAiMessages} typing={typing} onSend={sendAi} />
                )}

                {/* actions */}
                <div className="shrink-0 border-t border-burgundy/10 bg-cream px-4 pt-3 pb-2 flex gap-2">
                    <a
                        href="#book"
                        onClick={() => setOpen(false)}
                        className="grow inline-flex items-center justify-center gap-2 text-xs font-medium text-burgundy border border-burgundy/25 rounded-full py-2 hover:bg-burgundy hover:text-cream transition-colors"
                        data-testid="chatbot-book"
                    >
                        <Calendar className="w-3.5 h-3.5" />
                        Book Free Call
                    </a>
                    <a
                        href="#contact"
                        onClick={() => setOpen(false)}
                        className="grow inline-flex items-center justify-center gap-2 text-xs font-medium text-burgundy border border-burgundy/25 rounded-full py-2 hover:bg-burgundy hover:text-cream transition-colors"
                        data-testid="chatbot-contact"
                    >
                        <Mail className="w-3.5 h-3.5" />
                        Contact Team
                    </a>
                </div>
            </div>
        </>
    );
}

